import { useState, useEffect, useContext, useCallback } from 'react';
//import { getApolloContext } from '@apollo/react-common'
import { getApolloContext } from 'react-apollo';

const loginUrlWithReturn = () =>
  `${window.origin}/login?r=${encodeURIComponent(
    `${window.location.pathname}${window.location.search}`,
  )}`;

const catchUnauthenticatedError = e => {
  if (e.networkError?.statusCode === 401) {
    const bbPartner = localStorage.getItem('bb_partner') === 'true';
    if (bbPartner) {
      localStorage.removeItem('bb_partner');
      localStorage.removeItem('cn_ti_token');
      localStorage.removeItem('cn_user_data');
      window.location.assign('/bb-widget');
      return;
    }
    // Check if this is a CN Auth0 user
    const cnToken = localStorage.getItem('cn_ti_token');
    if (cnToken) {
      // Clear CN Auth0 tokens and redirect to cn-widget
      localStorage.removeItem('cn_ti_token');
      localStorage.removeItem('cn_user_data');
      window.location.assign('/cn-widget');
      return;
    }
    window.location.assign(loginUrlWithReturn());
  }
};

export function useClient() {
  const { client } = useContext(getApolloContext());
  return client;
}

export function useQuery(query, variables, deps = []) {
  const client = useClient();
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState();

  useEffect(() => {
    let active = true;
    if (!client) {
      return;
    }

    (async () => {
      setIsLoading(true);

      try {
        const { data } = await client.query({
          query,
          variables,
          fetchPolicy: 'no-cache',
        });
        if (!active) {
          return;
        }

        setData(data);
        setIsLoading(false);
      } catch (e) {
        catchUnauthenticatedError(e);
      }
    })();

    return () => {
      active = false;
    };
  }, [client, ...deps]);

  return isLoading ? null : data;
}

export function useQueryRefresh(
  query,
  variables,
  deps = [],
  displayGeneralError = true,
  skip = false,
) {
  const client = useClient();
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(!skip); // Set isLoading to false if skipping
  const [fetchPolicy, setFetchPolicy] = useState('no-cache');
  const [error, setError] = useState();
  const refresh = useCallback(() => {
    setFetchPolicy();
    setIsLoading(true);
  }, []);

  useEffect(() => {
    let active = true;
    if (!client || skip) {
      return; // Don't make the request if client is not available or skip is true
    }

    (async () => {
      setIsLoading(true);
      let response = {};
      try {
        response = await client.query({
          query,
          variables,
          fetchPolicy,
        });
      } catch (e) {
        console.warn(e);
        catchUnauthenticatedError(e);
        if (displayGeneralError) {
          const event = new CustomEvent('generalError', {
            detail: {
              error:
                'Something unexpected just happened. Please confirm reloading the page',
              title: 'Unexpected Error',
            },
          });
          document.getElementById('root').dispatchEvent(event);
        }
        setIsLoading(false);
        setError(e);
        return; // Return early in case of error
      }
      const data = response?.data;
      if (!active) return;
      setData(data);
      setError(null);
      setIsLoading(false);
      setFetchPolicy('no-cache');
    })();

    return () => {
      active = false;
    };
  }, [client, fetchPolicy, skip, ...deps]); // Adding skip to the deps array to trigger rerender when it changes

  return { data, refresh, isLoading, error };
}

const QUERY_RESULT_EVENT = 'queryresult';
const runningQueries = [];

export async function runQuery({ query, client, context }) {
  try {
    if (
      runningQueries.find(
        running => running.query === query && running.context === context,
      )
    ) {
      return;
    }
    runningQueries.push({ query, context });
    const { data } = await client.query({ query });

    window.dispatchEvent(
      new CustomEvent(QUERY_RESULT_EVENT, {
        detail: {
          query,
          result: data,
          context,
        },
      }),
    );

    const index = runningQueries.findIndex(
      running => running.query === query && running.context === context,
    );
    runningQueries.splice(index, 1);
  } catch (err) {
    catchUnauthenticatedError(err);
    console.log(err);
  }
}

export function useQueryResult(query, context, onResult, deps) {
  const handleQueryResult = useCallback(({ detail }) => {
    if (detail.query !== query) {
      return;
    }
    if (detail.context !== context) {
      console.log('Different context', detail.context, context);
      return;
    }

    onResult(detail.result);
  }, deps);
  useEffect(() => {
    window.addEventListener(QUERY_RESULT_EVENT, handleQueryResult);

    return () => {
      window.removeEventListener(QUERY_RESULT_EVENT, handleQueryResult);
    };
  }, deps);
}

export function useMutation() {
  const client = useClient();
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState();

  /*useEffect(() => {
    let active = true
    if (!client) {
      return
    }

    (async () => {
      setIsLoading(true)

      const { data } = await client.mutate({
        mutation,
        variables,
      })

      if (!active) {
        return
      }

      setData(data)
      setIsLoading(false)
    })()

    return () => {
      active = false
    }
  }, [client, ...deps])*/

  const mutate = (mutation, variables, onFinish, onError) => {
    let active = true;
    if (!client) {
      return;
    }

    (async () => {
      setIsLoading(true);
      try {
        const { data } = await client.mutate({
          mutation,
          variables,
        });
        if (!active) return;
        setData(data);
        setIsLoading(false);
        if (onFinish) {
          onFinish(data);
        }
      } catch (e) {
        setIsLoading(false);
        catchUnauthenticatedError(e);
        onError?.(e);
      }
    })();
    return () => {
      active = false;
    };
  };

  return {
    mutate,
    isLoading,
    data: isLoading ? null : data,
  };
}
